import * as React from "react";
import { toJS } from "mobx";
import { inject, observer } from "mobx-react";
import { UncontrolledTooltip } from "reactstrap";
import * as moment from "moment";
import * as fileUtil from "../../common/Methods/FileUtil";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCashRegister, faPrint, faPenSquare, faTrash, faDownload, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";

import {
    Settlement,
    PaymentMethodFriendlyName,
    CurrencyFriendlyName,
    SettlementTypeFriendlyName,
    SettlementType,
    Currency,
    PaymentMethod
} from "src/model/Financial";
import { NumberToMonetaryStringFormat } from "src/common/Methods/FileUtil";
import SettlementDialog from "./SettlementDialog";
import { CustomerStore } from "src/stores/CustomerStore";
import { SimpleConfirmation } from "../common/SimpleConfirmAlert";
import { successMessage, errorMessage } from "../common/Toast";
import LoadingWidget from "../common/LoadingWidget";

export interface State {
    showSettlementDialog: boolean;
    settlement: Settlement | undefined;
}

export interface Props {
    customerStore?: CustomerStore;
}

@inject("customerStore")
@observer
export default class SettlementsComponent extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            showSettlementDialog: false,
            settlement: undefined
        };
    }

    closeSettlementDialog = () => {
        this.setState({ showSettlementDialog: false, settlement: undefined });
    };

    deleteSettlement = async (id: number) => {
        try {
            const goAhead = await SimpleConfirmation(`Are you sure you'd like to remove this settlement?`);
            if (!goAhead) {
                return;
            }

            await this.props.customerStore!.deleteSettlement(id);
            successMessage(`This Settlement has been removed.`);
        } catch (error) {
            errorMessage(error);
        }
    };

    render() {
        const { customerStore } = this.props;
        if (!customerStore) {
            throw new Error(" ** No Customer Store **");
        }

        const customer = customerStore.customer;
        if (!customer) {
            return null;
        }

        const account = customer.account;

        const settlements = account.settlements.slice().sort((a: Settlement, b: Settlement) => b.dateCreated - a.dateCreated);
        const maxSettlementId = Math.max(...account.settlements.map(s => s.id));

        const columns = [
            {
                dataField: "dateCreated",
                text: "Date Created",
                formatter: (cellContent: any, row: Settlement) => (row.dateCreated ? moment(row.dateCreated).format("YYYY/MM/DD") : " - ")
            },
            {
                dataField: "createdLineItem.entryDate",
                text: "Transaction Date",
                formatter: (cellContent: any, row: Settlement) =>
                    row.createdLineItem ? moment(row.createdLineItem.entryDate).format("YYYY/MM/DD") : " - "
            },
            {
                dataField: "type",
                text: "Type",
                formatter: (cellContent: any, row: Settlement) =>
                    row.createdLineItem ? SettlementTypeFriendlyName(row.createdLineItem.type as SettlementType) : "-"
            },
            {
                dataField: "currency",
                text: "Currency",
                formatter: (cellContent: any, row: Settlement) => (row.createdLineItem ? row.createdLineItem.currency : "-")
            },
            {
                dataField: "amountInCents",
                text: "Amount (CAD$)",
                formatter: (cellContent: any, row: Settlement) =>
                    row.createdLineItem ? NumberToMonetaryStringFormat(row.createdLineItem.amountInCents) : "-"
            },
            {
                dataField: "paymentMethod",
                text: "Payment Method",
                formatter: (cellContent: any, row: Settlement) =>
                    row.createdLineItem ? PaymentMethodFriendlyName(row.createdLineItem.paymentMethod) : "-"
            },
            {
                dataField: "notes",
                text: "Notes",
                formatter: (cellContent: any, row: Settlement) => (row.notes ? row.notes : "-")
            },
            {
                dataField: "id",
                text: "Actions",
                headerStyle: { width: "60px" },
                formatExtraData: {
                    maxSettlementId
                },
                formatter: (cellContent: any, row: Settlement, rowIndex: number, extraData: { maxSettlementId: number }) => {
                    return (
                        <div style={{ width: "60px" }}>
                            <div className="btn-group btn-group-toggle mr-2">
                                <button
                                    onClick={() => this.setState({ settlement: row, showSettlementDialog: true })}
                                    id={`tooltipEdit${row.id}`}
                                    className="btn btn-secondary btn-sm"
                                    disabled={row.createdLineItem === null}
                                >
                                    <FontAwesomeIcon icon={faPenSquare} />
                                </button>
                                <UncontrolledTooltip placement="top" target={`tooltipEdit${row.id}`} delay={0} className="tooltipstrap">
                                    Edit Settlement
                                </UncontrolledTooltip>
                                <button
                                    onClick={() => this.deleteSettlement(row.id)}
                                    id={`tooltipDelete${row.id}`}
                                    className="btn btn-secondary btn-sm"
                                    disabled={row.id !== extraData.maxSettlementId}
                                >
                                    <FontAwesomeIcon icon={faTrashAlt} />
                                </button>
                                <UncontrolledTooltip placement="top" target={`tooltipDelete${row.id}`} delay={0} className="tooltipstrap">
                                    Delete Settlement
                                </UncontrolledTooltip>
                            </div>
                        </div>
                    );
                }
            }
        ];

        return (
            <>
                <div className="card shadow mb-5">
                    <div className="card-header">Settlements</div>
                    <div className="card-body">
                        <div className="table-responsive">
                            <BootstrapTable
                                keyField="id"
                                remote={false}
                                hover={true}
                                bordered={false}
                                columns={columns}
                                bootstrap4={true}
                                data={settlements}
                                classes="table table-sm"
                                pagination={paginationFactory()}
                                noDataIndication={() => (
                                    <div>
                                        <span>No results found.</span>
                                    </div>
                                )}
                            />
                        </div>
                    </div>
                </div>
                <SettlementDialog
                    editableSettlement={this.state.settlement}
                    showSettlementDialog={this.state.showSettlementDialog}
                    closeDialog={this.closeSettlementDialog}
                />
            </>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/customer/SettlementsComponent.tsx